import React, { useEffect, useRef, useState } from "react";
import FooterComponent from "../WebSite/Footer/FooterComponent";
import HeaderComponent from "../WebSite/Header/HeaderComponent";
import AboutBanner from "./AboutBanner";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400;1,500&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

  .os-root {
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: #1F2A24;
    background: #fff;
    font-size: 18px;
    line-height: 1.6;
  }

  .os-root * { box-sizing: border-box; }

  /* ── Hero ───────────────────────────────────── */
  .os-hero {
    background: #fff;
    border-bottom: 1px solid #CBD6CE;
    padding: 112px 64px 72px;
  }

  .os-hero-inner {
    max-width: 860px;
    margin: 0 auto;
  }

  .os-eyebrow {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #5B6660;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .os-eyebrow::after {
    content: '';
    display: block;
    width: 36px;
    height: 2px;
    background: #2F5D45;
    border-radius: 2px;
  }

  .os-hero h1 {
    font-family: 'Lora', serif;
    font-size: clamp(44px, 5.5vw, 56px);
    font-weight: 700;
    line-height: 1.08;
    color: #1F2A24;
    max-width: 780px;
    margin: 0 0 24px;
  }

  .os-hero-sub {
    font-size: 18px !important;
    color: #5B6660 !important;
    max-width: 580px;
    font-weight: 400 !important;
    line-height: 1.6 !important;
    margin: 0 !important;
  }

  /* ── Body ───────────────────────────────────── */
  .os-body {
    max-width: 860px;
    margin: 0 auto;
    padding: 0 64px 120px;
  }

  /* ── Section ────────────────────────────────── */
  .os-section {
    padding-top: 96px;
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 300ms ease-out, transform 300ms ease-out;
  }

  .os-section.os-visible {
    opacity: 1;
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    .os-section {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
    .os-rule {
      width: 60px !important;
      transition: none !important;
    }
  }

  .os-section h2 {
    font-family: 'Lora', serif;
    font-size: clamp(30px, 3.2vw, 36px);
    font-weight: 700;
    line-height: 1.2;
    color: #1F2A24;
    margin-bottom: 22px;
  }

  .os-section p {
    font-size: 18px;
    line-height: 1.6;
    color: #5B6660;
    font-weight: 400;
    margin-bottom: 18px;
    max-width: 720px;
  }

  .os-section p:last-child { margin-bottom: 0; }

  /* ── Rule ───────────────────────────────────── */
  .os-rule {
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #2F5D45, transparent);
    border: none;
    margin: 56px 0 0;
    transition: width 350ms ease-out;
  }

  .os-rule.os-visible { width: 60px; }

  /* ── Pilots ─────────────────────────────────── */
  .os-pilots {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin: 28px 0;
    max-width: 600px;
  }

  .os-pilot {
    background: #fff;
    border: 1px solid #CBD6CE;
    border-top: 3px solid #2F5D45;
    border-radius: 10px;
    padding: 24px;
    transition: box-shadow 0.2s, transform 0.2s;
  }

  .os-pilot:hover {
    box-shadow: 0 4px 20px rgba(47,93,69,0.10);
    transform: translateY(-2px);
  }

  .os-pilot-num {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #5B6660;
    margin-bottom: 8px;
  }

  .os-pilot p {
    font-size: 16px !important;
    line-height: 1.5 !important;
    color: #5B6660 !important;
    font-weight: 500 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  /* ── Discovery box ──────────────────────────── */
  .os-discovery {
    background: #fff;
    border: 1px solid #CBD6CE;
    border-left: 4px solid #2F5D45;
    border-radius: 0 12px 12px 0;
    padding: 28px 32px;
    margin: 32px 0;
    max-width: 680px;
  }

  .os-box-label {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #5B6660;
    margin-bottom: 14px;
  }

  .os-discovery > p {
    font-size: 17px !important;
    line-height: 1.6 !important;
    color: #1F2A24 !important;
    font-weight: 400 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  /* ── Field findings ─────────────────────────── */
  .os-findings {
    margin: 28px 0;
    max-width: 680px;
    border-top: 1px solid #CBD6CE;
  }

  .os-finding {
    display: flex;
    gap: 18px;
    align-items: flex-start;
    padding: 20px 0;
    border-bottom: 1px solid #CBD6CE;
  }

  .os-dot-wrap {
    padding-top: 10px;
    flex-shrink: 0;
  }

  .os-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2F5D45;
  }

  .os-finding p {
    font-size: 17px !important;
    line-height: 1.6 !important;
    color: #5B6660 !important;
    margin: 0 !important;
    max-width: 100% !important;
    font-weight: 400 !important;
  }

  /* ── Belief origin ──────────────────────────── */
  .os-belief-origin {
    background: linear-gradient(135deg, #E9F0EA 0%, #CBD6CE 100%);
    border-radius: 14px;
    padding: 36px 40px;
    margin: 32px 0;
    max-width: 680px;
    position: relative;
    overflow: hidden;
  }

  .os-belief-origin::before {
    content: '"';
    font-family: 'Lora', serif;
    font-size: 140px;
    color: #2F5D45;
    opacity: 0.12;
    position: absolute;
    top: -10px;
    left: 20px;
    line-height: 1;
    pointer-events: none;
  }

  .os-belief-origin > p {
    font-family: 'Lora', serif !important;
    font-size: 20px !important;
    font-style: italic !important;
    color: #2F5D45 !important;
    line-height: 1.6 !important;
    font-weight: 400 !important;
    margin: 0 !important;
    max-width: 100% !important;
    position: relative;
    z-index: 1;
  }

  /* ── Pull quote ─────────────────────────────── */
  .os-pullquote {
    border-left: 4px solid #2F5D45;
    background: #E9F0EA;
    padding: 24px 28px;
    margin: 32px 0;
    max-width: 680px;
    border-radius: 0 10px 10px 0;
  }

  .os-pullquote > p {
    font-family: 'Lora', serif !important;
    font-size: 20px !important;
    font-style: italic !important;
    color: #2F5D45 !important;
    line-height: 1.6 !important;
    font-weight: 400 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  /* ── Promise box ────────────────────────────── */
  .os-promise {
    border: 1.5px solid #2F5D45;
    border-radius: 14px;
    padding: 32px 36px;
    margin: 32px 0;
    max-width: 680px;
    background: #fff;
  }

  .os-promise > p {
    font-family: 'Lora', serif !important;
    font-size: 19px !important;
    font-style: italic !important;
    color: #1F2A24 !important;
    line-height: 1.6 !important;
    font-weight: 400 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  /* ── Vision / Mission ───────────────────────── */
  .os-vm-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin: 32px 0;
    max-width: 680px;
  }

  .os-vm-card {
    background: #fff;
    border: 1px solid #CBD6CE;
    border-radius: 14px;
    padding: 28px 26px;
    transition: box-shadow 0.2s, transform 0.2s;
  }

  .os-vm-card:hover {
    box-shadow: 0 4px 20px rgba(47,93,69,0.10);
    transform: translateY(-2px);
  }

  .os-vm-label {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #5B6660;
    margin-bottom: 12px;
  }

  .os-vm-card > p {
    font-size: 16px !important;
    line-height: 1.6 !important;
    color: #1F2A24 !important;
    font-weight: 400 !important;
    margin: 0 !important;
    max-width: 100% !important;
  }

  /* ── Journey note ───────────────────────────── */
  .os-journey-note {
    margin-top: 56px;
    padding: 32px 36px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid #CBD6CE;
    max-width: 680px;
  }

  .os-journey-note > p {
    font-size: 17px !important;
    font-style: italic !important;
    color: #5B6660 !important;
    line-height: 1.6 !important;
    font-weight: 400 !important;
    margin: 0 !important;
  }

  /* ── Responsive ─────────────────────────────── */
  @media (max-width: 640px) {
    .os-hero { padding: 64px 24px 56px; }
    .os-body { padding: 0 24px 80px; }
    .os-vm-grid { grid-template-columns: 1fr; }
    .os-pilots { grid-template-columns: 1fr; max-width: 100%; }
    .os-hero h1 { font-size: 34px; }
    .os-section p { font-size: 17px; }
    .os-belief-origin > p { font-size: 18px !important; }
    .os-pullquote > p { font-size: 18px !important; }
    .os-promise > p { font-size: 17px !important; }
  }
`;

/* ─── Hooks ──────────────────────────────────────────────────── */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // People who've asked their system for reduced motion see content
    // in place immediately, with no observer needed.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function OsSection({ children, delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`os-section${visible ? " os-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function OsRule() {
  const [ref, visible] = useInView(0.5);
  return <hr ref={ref} className={`os-rule${visible ? " os-visible" : ""}`} />;
}

/* ─── Main ───────────────────────────────────────────────────── */
export default function OurstoryComponent() {
  return (
    <>
      <style>{css}</style>
      <HeaderComponent />
      <AboutBanner />

      <div className="os-root">

        {/* ── Hero ── */}
        <div className="os-hero">
          <div className="os-hero-inner">
            <div className="os-eyebrow">Our Story</div>
            <h1>It started with a simple problem: agriculture needed better capital.</h1>
            <p className="os-hero-sub">
              A founder's journey through pilots, failures, field research across India,
              and the belief that changed everything.
            </p>
          </div>
        </div>

        <div className="os-body">

          {/* ── Chapter 1 ── */}
          <OsSection>
            <p>
              When we founded GBR, we were convinced the biggest problem in agribusiness
              was access to investment. Smallholder farmers and rural agribusinesses were
              sitting on productive assets that the world's investors couldn't see, couldn't
              trust, and couldn't reach. So we built for that gap.
            </p>
            <p>
              We created a mobile app for agri-investors to discover and invest in
              agricultural assets. We built a web platform for agribusinesses to define
              operating procedures and manage those assets through local entrepreneurs and
              approved farmers in rural areas. We put a mobile app in the hands of those
              farmers to execute on the ground.
            </p>
            <p>
              We ran real pilots — testing investable agri asset models to prove that rural
              agri assets could be structured, trusted, and made accessible to capital.
            </p>

            <div className="os-pilots">
              {[
                { num: "Pilot 01", label: "Sheep & Goat Farming" },
                { num: "Pilot 02", label: "Milking Cow Operations" },
                { num: "Pilot 03", label: "Bee Colonies" },
              ].map((p) => (
                <div className="os-pilot" key={p.num}>
                  <div className="os-pilot-num">{p.num}</div>
                  <p>{p.label}</p>
                </div>
              ))}
            </div>

            <p>It was a clean model. And it taught us something we weren't expecting.</p>
          </OsSection>

          <OsRule />

          {/* ── Chapter 2 ── */}
          <OsSection delay={100}>
            <h2>The real problem wasn't capital. It was structure.</h2>
            <p>
              As we ran pilots and went deeper into how agribusinesses actually operated,
              a pattern emerged that we couldn't ignore. The investors were willing. The
              farmers were capable. But the businesses in the middle — the agribusinesses
              trying to coordinate it all — were operating on broken foundations.
            </p>
            <p>
              No clear structure. No governed relationships. No way to scale trust across
              a network of partners, farmers, contractors, and distributors. Every attempt
              to grow meant manually rebuilding that trust from scratch.
            </p>
            <p>Capital couldn't fix that. Infrastructure could.</p>
          </OsSection>

          <OsRule />

          {/* ── Chapter 3 ── */}
          <OsSection delay={100}>
            <h2>So we tried to operate the ecosystem ourselves.</h2>
            <p>
              We didn't just observe the problem — we stepped into it. We attempted to run
              the ecosystem on behalf of agribusinesses: coordinating partners, structuring
              workflows, managing the network of actors that each business depended on to
              function.
            </p>
            <p>And that's when we discovered the most important thing of all.</p>

            <div className="os-discovery">
              <div className="os-box-label">The Real Discovery</div>
              <p>
                Agribusinesses didn't want someone else to run their ecosystem. They wanted
                to run it themselves. The will was there. What was missing was the
                technology, the systems, and the processes to make it possible. No tool
                existed that could give them the structure, governance, and scale they
                needed to operate their own network — on their own terms.
              </p>
            </div>

            <p>
              They weren't waiting for an operator. They were waiting for the infrastructure
              to become operators themselves.
            </p>
          </OsSection>

          <OsRule />

          {/* ── Chapter 4 ── */}
          <OsSection delay={100}>
            <h2>Then I took it to the field — across India.</h2>
            <p>
              Armed with that discovery, I started going deeper. I met large and mid-market
              agribusinesses at agriculture, livestock, agri-tech, and food-tech shows and
              expos across India — in every segment, at every scale. Hundreds of
              conversations. The same pattern, everywhere.
            </p>
            <p>
              Every one of these businesses had vendors and partners. But those relationships
              were unorganised, undigitised, and unmonetised. And almost every agribusiness
              — regardless of size — was operating as a participant in someone else's
              ecosystem platform, not as an operator of their own.
            </p>
            <p>When I asked why, the answers were always the same:</p>

            <div className="os-findings">
              {[
                "Revenue and sales were the immediate priority — there was no time or headspace to think about becoming an ecosystem operator.",
                "There was no system, no process, and no technology through which they could operate their own ecosystem and start earning from it quickly.",
                "They knew their partner and vendor networks held untapped value — but had no way to structure or monetise those relationships at scale.",
                "In short: they were not empowered. The capability was there. The intent was there. The infrastructure was not.",
              ].map((text, i) => (
                <div className="os-finding" key={i}>
                  <div className="os-dot-wrap"><div className="os-dot" /></div>
                  <p>{text}</p>
                </div>
              ))}
            </div>

            <p style={{ marginTop: "20px" }}>That was the moment everything crystallised.</p>

            <div className="os-belief-origin">
              <div className="os-box-label">Where the Belief Was Born</div>
              <p>
                Every agribusiness — regardless of size — has the right to own, operate,
                and monetise its own ecosystem. Not as a participant in someone else's
                network. As the operator of their own. And this belief is why GBR exists.
              </p>
            </div>
          </OsSection>

          <OsRule />

          {/* ── Chapter 5 ── */}
          <OsSection delay={100}>
            <h2>That's when the belief formed.</h2>
            <p>
              Through every pilot, every failure, every conversation with agribusinesses
              trying to scale — a conviction took shape that we couldn't shake. We stopped
              building for investors and stopped trying to operate on behalf of
              agribusinesses. We started building for what they actually needed. And the
              belief that emerged from that journey became the foundation of everything we
              do today:
            </p>

            <div className="os-pullquote">
              <p>
                Every enterprise — regardless of size — needs to own, build, and operate
                its own structured ecosystem digitally.
              </p>
            </div>

            <p>
              Not use someone else's platform. Not plug into someone else's network. Own
              it. Operate it. Build on it. Because a business that doesn't own its
              ecosystem doesn't really control its future.
            </p>
          </OsSection>

          <OsRule />

          {/* ── Chapter 6 ── */}
          <OsSection delay={100}>
            <h2>From agribusiness insight to enterprise infrastructure.</h2>
            <p>
              That belief drove us to build differently. Instead of a SaaS product that
              agribusinesses would depend on us to run, we began building the infrastructure
              and frameworks that any agribusiness — any enterprise — could take ownership
              of. A technology layer they could deploy on their own terms, extend with their
              own teams, and use to bring structure, trust, and scale to their entire network
              of internal and external actors.
            </p>
            <p>Our promise to agribusiness — and to every enterprise we work with — is this:</p>

            <div className="os-promise">
              <p>
                You will not just use infrastructure. You will own your structured ecosystem
                — digitally, completely, and on your terms. The technology, the systems, and
                the processes will be yours — so you can build, operate, and grow your
                network the way you always knew you could.
              </p>
            </div>
          </OsSection>

          <OsRule />

          {/* ── Vision & Mission ── */}
          <OsSection delay={100}>
            <div className="os-vm-grid">
              <div className="os-vm-card">
                <div className="os-vm-label">Vision · 2035</div>
                <p>
                  Every agribusiness becomes an ecosystem operator — owning, managing,
                  and monetizing its own network.
                </p>
              </div>
              <div className="os-vm-card">
                <div className="os-vm-label">Mission</div>
                <p>
                   Build ecosystem infrastructure that enables agrifood organisation to orchestrate, build , govern, opearte  and monetize the their own ecosystem network..
                </p>
              </div>
            </div>
          </OsSection>

          <OsRule />

          {/* ── Where we are today ── */}
          <OsSection delay={100}>
            <h2>Where we are today.</h2>
            <p>
              What began in the fields of rural agribusiness has evolved into a platform for agrifood enterprise
              ecosystem orchestration — giving mid-market , upper-mid-market and large agriffod the technology to build, own,
              and operate their own sovereign ecosystem network of economic agents: partners, suppliers,
              distributors, contractors, and AI agents, all governed under a single trusted
              layer they control.
            </p>
            <p>
              Every agrifood organisation will become an ecosystem operator. The ones who own and
              operate it will define the market. GBR makes it possible.
            </p>

            <div className="os-journey-note">
              <p>
                We are still on this journey — finding the right fit, deepening our
                understanding, and building alongside the agribusinesses and agrifood enterprises
                that dare to own their future.
              </p>
            </div>
          </OsSection>

        </div>{/* os-body */}
      </div>{/* os-root */}

     {/*  <FooterComponent /> */}
    </>
  );
}
